import { Tree, TreeNode } from '@/shared/lib/utils/tree/types';

export const filterTree = <
  T extends Record<string, any>,
  R extends (
    val: TreeNode<T, K>,
    options?: {
      depth?: number;
      parent?: TreeNode<T, K>;
      index?: number;
      path?: TreeNode<T, K>[];
    },
  ) => boolean,
  K extends (keyof T & string) | string = 'children',
>(
  tree: T[],
  childrenKey = 'children',
  rule: R,
): Tree<T, K> => {
  const helper = (
    node: TreeNode<T, K>,
    options?: {
      depth?: number;
      parent?: TreeNode<T, K>;
      index?: number;
      path?: TreeNode<T, K>[];
    },
  ): TreeNode<T, K> | null => {
    const { depth = 0, parent = null, index = 0, path = [] } = options || {};

    // Recursively filter children if they exist
    const filteredChildren = node?.[childrenKey]
      ? (node[childrenKey] as TreeNode<T, K>[])
          .map((child, i) =>
            helper(child, {
              depth: depth + 1,
              parent: node,
              index: i,
              path: [...path, node],
            }),
          )
          .filter((child) => child !== null)
      : undefined;

    // If rule matches or any children remain after filtering
    if (
      rule(node, { depth, parent, index, path }) ||
      (filteredChildren && filteredChildren.length > 0)
    ) {
      if (filteredChildren && filteredChildren.length > 0) {
        return { ...node, [childrenKey]: filteredChildren };
      } else {
        return { ...node, [childrenKey]: undefined };
      }
    }

    return null;
  };

  return tree
    .map((node, i) =>
      helper(node, {
        depth: 0,
        parent: null,
        index: i,
        path: [],
      }),
    )
    .filter((node) => node !== null);
};